import type * as React from 'react';
import { getPaginated } from '@Domain/Api';
import organizationsFetched, {
	type OrganizationPayload,
	type OrganizationsFetchedAction,
} from '@Application/Organizations/Organizations/Actions/organizationsFetched';
import organizationsFetchFailed, {
	type OrganizationsFetchFailedAction,
} from '@Application/Organizations/Organizations/Actions/organizationsFetchFailed';
import type FetchOrganizations from '@Application/Organizations/Organizations/Queries/FetchOrganizations';
import ApiError from '@Infra/FetchLib/ApiError';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = OrganizationsFetchedAction | OrganizationsFetchFailedAction;

// `getPaginated` requires a mutable `results` array (it pushes subsequent pages into it); the
// dispatched action still carries the `readonly` form declared by `OrganizationsFetchedPayload`.
type PaginatedOrganizations = {
	totalRecords: number;
	results: OrganizationPayload[];
};

/**
 * Walks every page (organizations are a bounded per-account catalogue, like facilities and roles):
 * the store holds the whole list, and any search/sort the screen offers filters it client-side.
 */
export default (query: FetchOrganizations) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Page indexing starts at 0 server-side, and `getPaginated` only walks every page when called
			// with `page: 0` — its internal guard only catches `page > 1`. `rowsPerPage: 200` is the
			// server's maximum accepted `rows` value.
			const response = await getPaginated<PaginatedOrganizations>(
				`${parameters.api.host}/api/accounts/${query.accountId}/organizations`,
				{ page: 0, rowsPerPage: 200 },
			);

			const action = organizationsFetched(response.results, query.accountId);
			dispatch(action);
			return action;
		} catch (error) {
			const status = error instanceof ApiError ? error.status : null;
			const action = organizationsFetchFailed(extractMessages(error), status);
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
